import { type ServiceUniqueName } from '@bv/api';
import { getRouter } from '@bv/plugins/router';
import { reactive } from 'vue';

import { aiAgentApi, aiAgentVoiceApi } from '@bvai/api';

interface BvaiConfigWithService extends BvaiConfig {
  service: ServiceUniqueName;
}

const _state = reactive<{ config: BvaiConfigWithService | null }>({ config: null });

const applyApiPrefix = (): void => {
  const prefix = import.meta.env.VITE_CURRENT_SERVICE_UNIQUE_NAME;
  if (!prefix) {
    return;
  }

  aiAgentApi.endpoint = `${prefix}/${aiAgentApi.endpoint}`;
  aiAgentVoiceApi.endpoint = `${prefix}/${aiAgentVoiceApi.endpoint}`;
};

export const defineBvaiConfig = (config: BvaiConfig): void => {
  if (_state.config) {
    // eslint-disable-next-line no-console
    console.warn('[bvai] Config already defined. Overwriting.');
  }

  const service = import.meta.env.VITE_CURRENT_SERVICE_UNIQUE_NAME;
  if (!service) {
    throw new Error('[bvai] Service unique name is not defined.');
  }

  _state.config = {
    service,
    ...config,
  };

  applyApiPrefix();

  setTimeout(() => {
    filterRoutes(config.exclude_routes);
  }, 0);
};

const filterRoutes = (routesNames?: BvaiRouteNames[]): void => {
  if (!routesNames) {
    return;
  }

  const router = getRouter();
  if (!router) {
    return;
  }

  const routeNamesToRemove = new Set<string>();
  const routeTree = router.getRoutes();

  const collectChildrenNames = (route: (typeof routeTree)[number]): void => {
    if (route.name && typeof route.name === 'string') {
      routeNamesToRemove.add(route.name);
    }

    const children = route.children ?? [];
    children.forEach((child) => {
      collectChildrenNames(child as (typeof routeTree)[number]);
    });
  };

  routeTree.forEach((route) => {
    if (!route.name || typeof route.name !== 'string') {
      return;
    }

    if (routesNames.includes(route.name as BvaiRouteNames)) {
      collectChildrenNames(route);
    }
  });

  routeNamesToRemove.forEach((routeName) => {
    if (router.hasRoute(routeName)) {
      router.removeRoute(routeName);
    }
  });
};

export const getBvaiConfig = (): BvaiConfigWithService | null => {
  return _state.config;
};
